import { useTranslation } from "react-i18next";
import { apiRequest } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import type { NotificationVariant } from "@/components/tables/hooks/useTableNotifications";

export interface UseBulkActionsOptions {
  entity: string;
  companySubjectId?: string;
  refreshTableAfterAction?: () => void;
  notify: (message: any, variant: NotificationVariant) => void;
  requestReload: () => void;
  clearRowSelection: () => void;
  onError: (error: any) => void;
}

type SelectedRow = Record<string, any>;

export function useBulkActions(options: UseBulkActionsOptions) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const impersonationParameters = () =>
    options.companySubjectId
      ? { impersonate_subject: options.companySubjectId }
      : {};

  const bulkDelete = async (
    selectedRows: SelectedRow[],
    skipQuestion = false
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("deleted_selected_confirm", { number: selectedRows.length })
      );
      if (!isConfirmed) {
        return;
      }
    }

    try {
      await Promise.all(
        selectedRows.map((row) =>
          apiRequest({
            method: "delete",
            entity: options.entity,
            entityId: row.id,
            additionalRouteParts: {},
            parametersToRender: {
              additionalUrlParameters: impersonationParameters(),
              depth: "0",
            },
          })
        )
      );
      options.clearRowSelection();
      options.notify(t("delete_successful"), "success");
      options.requestReload();
      options.refreshTableAfterAction?.();
    } catch (error) {
      options.onError(error);
    }
  };

  return { bulkDelete };
}
